import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

const ChatContext = createContext();

export const ChatProvider = ({ children, token, userId }) => {
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState({});
  const [contacts, setContacts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState({});
  const [showChat, setShowChat] = useState(false);
  
  const ws = useRef(null);
  const reconnectTimeout = useRef(null);
  const typingTimeout = useRef({});
  const tokenRef = useRef(token); // Mantener referencia actualizada del token
  
  // Actualizar tokenRef cuando cambia el token
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  // WebSocket connection
  const connectWebSocket = useCallback(() => {
    if (!token) return;

    try {
      ws.current = new WebSocket(`${WS_URL}/api/chat/ws/${token}`);

      ws.current.onopen = () => {
        console.log('✅ WebSocket connected');
        setIsConnected(true);
      };

      ws.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        
        if (data.type === 'new_message') {
          const message = data.message;
          
          // Agregar mensaje a la lista
          setMessages(prev => ({
            ...prev,
            [message.conversation_id]: [
              ...(prev[message.conversation_id] || []),
              message
            ]
          }));
          
          // Actualizar conversación
          setConversations(prev => {
            const updated = prev.map(conv => {
              if (conv.id === message.conversation_id) {
                return {
                  ...conv,
                  last_message: message.content,
                  last_message_at: message.created_at,
                  unread_count: message.sender_id === userId ? 0 : (conv.unread_count || 0) + 1
                };
              }
              return conv;
            });
            
            // Ordenar por último mensaje
            return updated.sort((a, b) => 
              new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0)
            );
          });
          
          // Reproducir sonido de notificación SOLO si el mensaje es de otra persona
          // Verificamos que sender_id exista, userId exista y sean diferentes
          if (message.sender_id && userId && message.sender_id !== userId) {
            playNotificationSound();
          }
        }
        
        if (data.type === 'typing') {
          setTypingUsers(prev => ({
            ...prev,
            [data.conversation_id]: data.user_id
          }));
          
          // Limpiar después de 3 segundos
          if (typingTimeout.current[data.conversation_id]) {
            clearTimeout(typingTimeout.current[data.conversation_id]);
          }
          typingTimeout.current[data.conversation_id] = setTimeout(() => {
            setTypingUsers(prev => {
              const updated = { ...prev };
              delete updated[data.conversation_id];
              return updated;
            });
          }, 3000);
        }
      };

      ws.current.onclose = () => {
        console.log('❌ WebSocket disconnected');
        setIsConnected(false);
        
        // Reconectar después de 3 segundos
        reconnectTimeout.current = setTimeout(() => {
          connectWebSocket();
        }, 3000);
      };

      ws.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
  }, [token, userId]);

  useEffect(() => {
    connectWebSocket();
    
    return () => {
      if (ws.current) {
        ws.current.close();
      }
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }
    };
  }, [connectWebSocket]);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/chat/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data.conversations);
      
      // Calcular total de no leídos
      const total = response.data.conversations.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
      setUnreadCount(total);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  }, [token]);

  // Fetch contacts
  const fetchContacts = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/chat/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(response.data.contacts);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  }, [token]);

  // Fetch messages
  const fetchMessages = useCallback(async (conversationId) => {
    if (!token) return;
    
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/chat/conversations/${conversationId}/messages`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(prev => ({
        ...prev,
        [conversationId]: response.data.messages
      }));
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }, [token]);

  // Send message
  const sendMessage = useCallback((conversationId, content, messageType = 'text', fileUrl = null) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({
        type: 'message',
        conversation_id: conversationId,
        content: content,
        message_type: messageType,
        file_url: fileUrl
      }));
    }
  }, []);

  // Send typing indicator
  const sendTyping = useCallback((conversationId) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({
        type: 'typing',
        conversation_id: conversationId
      }));
    }
  }, []);

  // Mark as read
  const markAsRead = useCallback(async (conversationId) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({
        type: 'read',
        conversation_id: conversationId
      }));
    }
    
    // Actualizar contador local
    setConversations(prev => prev.map(conv => 
      conv.id === conversationId ? { ...conv, unread_count: 0 } : conv
    ));
  }, []);

  // Create conversation
  const createConversation = useCallback(async (participantId, participantType) => {
    if (!token) return null;
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/chat/conversations`,
        { participant_id: participantId, participant_type: participantType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refrescar conversaciones
      await fetchConversations();
      
      return response.data;
    } catch (error) {
      console.error('Error creating conversation:', error);
      throw error;
    }
  }, [token, fetchConversations]);

  // Upload file
  const uploadFile = useCallback(async (file) => {
    const currentToken = tokenRef.current; // Usar token de la ref
    
    if (!currentToken) {
      console.error('❌ No token available for upload');
      throw new Error('No token available');
    }
    
    console.log('🔍 Uploading file with token:', currentToken.substring(0, 20) + '...');
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/chat/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      console.log('✅ File uploaded successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error uploading file:', error.response?.data || error.message);
      throw error;
    }
  }, []); // Sin dependencias - siempre usa tokenRef.current

  // Play notification sound
  const playNotificationSound = () => {
    const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIGGS56+qjUBELUqHh8LNgGwU7k9nxy3krBSh+zPLaizsKFFGn5e2rWBQJPJLV8MRyJgUki8X03o8+CxxdtvLqnlEPDFKj4/C1YhsGOpLY8cp5KwUnh8rx3I4/CRZYr+fxrnEtBz2a3PLHeisFKIXD89qOPgsZXLXt6aFPEQ1Poe/ytWUeBT2P1vDLfC0FKoXD8tqNPgoYWrDn8K9xLgU+mt3yxnorBSiDwvLajj4LGV212+mfTxENTaHu8bVlHgU9j9bwy3wuBSmFw/Lajj4KGFyx5/KvcS4FP5rd8sV6KwUnhcPy2o0/ChlctdvpoE8SDU2h7vG1ZR4FPY/W8Mt8LgUshcPy2o4+ChhcrufyrnEuBT+a3fLFeisFKIXD8tqNPgoZXLXb6aBPEg1Noe3xtWUeBT2P1vDLfC4FKoXD8tqOPgoYXK7n8q1xLgU/m93yxHkrBSiFw/Lajj4LGV212+mgTxINTaHt8bVlHgU9j9bwy3wuBSqFw/Lajj4KGFyu5/KtcS4FP5rd8sN5KwUohcPy2o4+Cxlctdvpn08SDU2h7fG1ZR4FPY/W8Mt8LgUqhcPy2o4+ChhcrufyrnEuBT+a3fLDeSsFKIXD8tqOPgsZXLXb6Z9PEg1Noe3xtWUeBT2P1vDLfC4FKoXD8tqOPgoYXK7n8q5xLgU/mt3yw3krBSiFw/Lajj4LGVy12+mfTxINTaHt8bVlHgU9j9bwy3wuBSqFw/Lajj4KGFyu5/KucS4FP5rd8sN5KwUohcPy2o4+Cxlctdvpn08SDU2h7fG1ZR4FPY/W8Mt8LgUqhcPy2o4+ChhcrufyrnEuBT+a3fLDeSsFKIXD8tqOPgsZXLXb6Z9PEg1Noe3xtWUeBT2P1vDLfC4FKoXD8tqOPgoYXK7n8q5xLgU/mt3yw3krBSiFw/Lajj4LGVy12+mfTxINTaHt8bVlHgU9j9bwy3wuBSqFw/Lajj4KGFyu5/KucS4FP5rd8sN5KwUohcPy2o4+Cxlctdvpn08SDU2h7fG1ZR4FPY/W8Mt8LgUqhcPy2o4+ChhcrufyrnEuBT+a3fLDeSsFKIXD8tqOPgsZXLXb6Z9PEg1Noe3xtWUeBT2P1vDLfC4FKoXD8tqOPgoYXK7n8q5xLgU/mt3yw3krBSiFw/Lajj4LGVy12+mfTxINTaHt8bVlHgU9j9bwy3wuBSqFw/Lajj4KGFyu5/KucS4FP5rd8sN5KwUohcPy2o4+Cxlctdvpn08SDU2h7fG1ZR4FPY/W8Mt8LgUqhcPy2o4+ChhcrufyrnEuBT+a3fLDeSsFKIXD8tqOPg==');
    audio.volume = 0.3;
    audio.play().catch(() => {});
  };

  useEffect(() => {
    if (token) {
      fetchConversations();
      fetchContacts();
    }
  }, [token, fetchConversations, fetchContacts]);

  const value = {
    conversations,
    activeConversation,
    setActiveConversation,
    messages,
    contacts,
    unreadCount,
    isConnected,
    typingUsers,
    showChat,
    setShowChat,
    sendMessage,
    sendTyping,
    markAsRead,
    createConversation,
    uploadFile,
    fetchMessages,
    fetchConversations
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within ChatProvider');
  }
  return context;
};
